import * as React from 'react'
import clsx from 'clsx'
import { Call, Sms } from 'iconsax-react'

import AddChannel from '../EHAccordion/ProfileV2/AddChannel'
import ChannelRow from './Row'
import { useCopyableState } from '@SonarCX/providers/CopyableStateProvider'
import { getFormatted } from '@SonarCX/utilities/phoneNumber'

import useStyles from './styles'

interface UserChannelsProps {
  user: any
  canAdd?: boolean
  canChangeChannel?: boolean
  handleChannelClick?: CallableFunction
  showVerifiedIcon?: boolean
  clickOnEdit?: boolean
  showOptions?: boolean
  hideIcon?: boolean
  onDelete?: (channel: APIChannel) => void
  onChannelAdd: () => void
}

const UserChannels: React.FC<UserChannelsProps> = ({
  user,
  canChangeChannel = false,
  canAdd,
  handleChannelClick,
  showVerifiedIcon,
  clickOnEdit,
  showOptions,
  hideIcon = false,
  onChannelAdd,
  onDelete
}) => {
  const { setText, copyToClipboard } = useCopyableState()
  const [addingPhone, setAddingPhone] = React.useState(false)
  const [addingEmail, setAddingEmail] = React.useState(false)
  const [editingChannel, setEditingChannel] = React.useState(null)
  const canDelete = Boolean(onDelete)

  const classes = useStyles({ canChangeChannel, onDelete: canDelete })

  // Reset adding state on user switch
  React.useEffect(() => {
    setAddingEmail(false)
    setAddingPhone(false)
  }, [user?.id])

  // Find channels for this user
  const [emailChannels, phoneChannels] = React.useMemo(() => {
    if (!user?.channels) {
      return [[], []]
    }

    const emails = []
    const phones = []

    user.channels.forEach(ch => {
      const type = ch.type
      if (type === 'email') {
        emails.push(ch)
      } else if (['sms_voice', 'voice_only'].includes(type)) {
        phones.push(ch)
      }
    })

    return [emails, phones]
  }, [user])

  const handleOnChannelClick = React.useCallback(
    (channel, isPhone, valid) => {
      if (clickOnEdit) {
        setText(isPhone ? getFormatted(channel.value) : channel.value)
        copyToClipboard()
        return
      }
      if (valid) {
        handleChannelClick && handleChannelClick(isPhone, channel.id)
      }
    },
    [clickOnEdit, handleChannelClick, copyToClipboard, setText]
  )

  return (
    <>
      {/* Phone channel sections */}
      <div className={classes.channelBlock}>
        <div className={classes.row}>
          {!hideIcon && (
            <Call variant='Bold' size='16' className={classes.icon} />
          )}

          <div className={classes.title}>Phone</div>

          {canAdd && (
            <div
              onClick={() => setAddingPhone(true)}
              className={clsx(
                classes.addButton,
                addingPhone && classes.addButtonFaded
              )}
            >
              Add
            </div>
          )}
        </div>

        {phoneChannels.map(ch => (
          <ChannelRow
            isPhone={true}
            showOptions={showOptions}
            showVerifiedIcon={showVerifiedIcon}
            user={user}
            key={ch?.id}
            channel={ch}
            editingChannel={editingChannel}
            canDelete={canDelete}
            onChannelAdd={onChannelAdd}
            setEditingChannel={setEditingChannel}
            handleOnChannelClick={handleOnChannelClick}
            canChangeChannel={canChangeChannel}
            onDelete={onDelete}
          />
        ))}

        {addingPhone && (
          <AddChannel
            channels={user?.channels}
            onCancel={() => setAddingPhone(false)}
            onAdd={onChannelAdd}
            isPhone
            canDelete={canDelete}
          />
        )}
      </div>

      {/* Email channel section */}
      <div className={classes.channelBlock}>
        <div className={classes.row}>
          {!hideIcon && (
            <Sms variant='Bold' size='16' className={classes.icon} />
          )}
          <div className={classes.title}>Email</div>

          {canAdd && (
            <div
              onClick={() => setAddingEmail(true)}
              className={clsx(
                classes.addButton,
                addingEmail && classes.addButtonFaded
              )}
            >
              Add
            </div>
          )}
        </div>

        {emailChannels.map(ch => (
          <ChannelRow
            isPhone={false}
            showOptions={showOptions}
            showVerifiedIcon={showVerifiedIcon}
            user={user}
            key={ch?.id}
            channel={ch}
            editingChannel={editingChannel}
            canDelete={canDelete}
            onChannelAdd={onChannelAdd}
            setEditingChannel={setEditingChannel}
            handleOnChannelClick={handleOnChannelClick}
            canChangeChannel={canChangeChannel}
            onDelete={onDelete}
          />
        ))}

        {addingEmail && (
          <AddChannel
            channels={user?.channels}
            onCancel={() => setAddingEmail(false)}
            onAdd={onChannelAdd}
            canDelete={canDelete}
          />
        )}
      </div>
    </>
  )
}
export default UserChannels
